import React, { useEffect, useState } from 'react';
import { format } from 'date-fns';
import Icon from '../../components/AppIcon';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { useToast } from '../../context/ToastContext';
import { fetchAllEscrows, releaseEscrowFunds, getEscrowById, resolveOrderDispute } from '../../lib/adminApi';
import { exportToCsv, downloadFilename } from '../../lib/csvExport';

const escrowStatusFilters = [
  { value: 'all', label: 'All Escrows' },
  { value: 'pending', label: 'Pending' },
  { value: 'escrowed', label: 'Escrowed' },
  { value: 'released', label: 'Released' },
  { value: 'disputed', label: 'Disputed' },
];

const statusStyles = {
  pending: 'bg-amber-100 text-amber-700',
  escrowed: 'bg-blue-100 text-blue-700',
  released: 'bg-emerald-100 text-emerald-700',
  disputed: 'bg-rose-100 text-rose-700',
  refunded: 'bg-purple-100 text-purple-700',
  cancelled: 'bg-gray-100 text-gray-700',
};

const escrowExportColumns = [
  { key: 'id', label: 'Escrow ID' },
  { key: 'order_id', label: 'Order ID' },
  { key: 'caterer_name', label: 'Caterer' },
  { key: 'gross_amount', label: 'Gross' },
  { key: 'commission_amount', label: 'Commission' },
  { key: 'caterer_net_amount', label: 'Net' },
  { key: 'escrow_status', label: 'Status' },
  { key: 'created_at', label: 'Created' },
];

export default function EscrowsPage() {
  const { profile, session } = useAdminAuth();
  const toast = useToast();
  const [statusFilter, setStatusFilter] = useState('all');
  const [escrows, setEscrows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedEscrow, setSelectedEscrow] = useState(null);
  const [showReleaseModal, setShowReleaseModal] = useState(false);
  const [releaseReason, setReleaseReason] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    let isMounted = true;

    async function loadEscrows() {
      setLoading(true);
      setError(null);

      try {
        const data = await fetchAllEscrows({ status: statusFilter === 'all' ? undefined : statusFilter });
        if (isMounted) {
          setEscrows(data);
        }
      } catch (err) {
        console.error('Failed to load escrows', err);
        if (isMounted) {
          setError(err instanceof Error ? err.message : 'Failed to load escrows');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    loadEscrows();

    return () => {
      isMounted = false;
    };
  }, [statusFilter]);

  const handleReleaseEscrow = async () => {
    if (!selectedEscrow || !releaseReason.trim()) {
      toast.error('Please provide a release reason');
      return;
    }

    setProcessing(true);
    try {
      await releaseEscrowFunds(selectedEscrow.id, profile?.user_id, releaseReason, session?.access_token);
      toast.success('Escrow released successfully');
      setShowReleaseModal(false);
      setSelectedEscrow(null);
      setReleaseReason('');
      const data = await fetchAllEscrows({ status: statusFilter === 'all' ? undefined : statusFilter });
      setEscrows(data);
    } catch (err) {
      toast.error(err?.message || 'Failed to release escrow');
    } finally {
      setProcessing(false);
    }
  };

  const handleResolveDispute = async (escrowId) => {
    const reason = window.prompt('Resolution reason (optional):', 'Resolved by admin') || 'Resolved by admin';
    setProcessing(true);
    try {
      await resolveOrderDispute(escrowId, profile?.user_id, reason, session?.access_token);
      toast.success('Dispute resolved. Escrow can now be released.');

      const data = await fetchAllEscrows({
        status: statusFilter === 'all' ? undefined : statusFilter,
      });
      setEscrows(data);
    } catch (err) {
      toast.error(err?.message || 'Failed to resolve dispute');
    } finally {
      setProcessing(false);
    }
  };

  const escrowedTotal = escrows
    .filter((e) => e.escrow_status === 'escrowed')
    .reduce((sum, e) => sum + (Number(e.caterer_net_amount) || 0), 0);

  const releasedTotal = escrows
    .filter((e) => e.escrow_status === 'released')
    .reduce((sum, e) => sum + (Number(e.release_amount) || 0), 0);

  return (
    <div className="space-y-6">
      <header className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-foreground">Escrow Management</h2>
          <p className="text-sm text-muted-foreground">
            Monitor all escrow transactions, release funds, and manage disputes.
          </p>
        </div>
        <button
          onClick={() => {
            const rows = escrows.map((e) => ({
              id: e.id,
              order_id: e.order_id ?? e.order?.id ?? '',
              caterer_name: e.caterer?.business_name ?? '',
              gross_amount: e.gross_amount,
              commission_amount: e.commission_amount,
              caterer_net_amount: e.caterer_net_amount,
              escrow_status: e.escrow_status ?? '',
              created_at: e.created_at ? format(new Date(e.created_at), 'yyyy-MM-dd HH:mm') : '',
            }));
            if (!rows.length) {
              toast.info('No escrows to export');
              return;
            }
            exportToCsv(rows, escrowExportColumns, downloadFilename('escrows'));
            toast.success(`Exported ${rows.length} escrows`);
          }}
          className="inline-flex items-center gap-2 rounded-md border border-border bg-card px-3 py-2 text-sm font-medium text-foreground shadow-sm hover:bg-muted"
        >
          <Icon name="Download" size={16} /> Export
        </button>
      </header>

      {/* Summary Cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-xl border border-border bg-card p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Total Escrows</p>
              <p className="mt-2 text-2xl font-semibold text-foreground">{escrows.length}</p>
            </div>
            <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Icon name="Lock" size={22} />
            </span>
          </div>
        </div>
        <div className="rounded-xl border border-border bg-card p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Escrowed Balance</p>
              <p className="mt-2 text-2xl font-semibold text-foreground">R {escrowedTotal.toLocaleString()}</p>
            </div>
            <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-blue-100 text-blue-600">
              <Icon name="Wallet" size={22} />
            </span>
          </div>
        </div>
        <div className="rounded-xl border border-border bg-card p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Released Total</p>
              <p className="mt-2 text-2xl font-semibold text-foreground text-emerald-600">R {releasedTotal.toLocaleString()}</p>
            </div>
            <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-emerald-100 text-emerald-600">
              <Icon name="CheckCircle" size={22} />
            </span>
          </div>
        </div>
        <div className="rounded-xl border border-border bg-card p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Pending Release</p>
              <p className="mt-2 text-2xl font-semibold text-foreground text-amber-600">
                {escrows.filter((e) => e.escrow_status === 'escrowed').length}
              </p>
            </div>
            <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-amber-100 text-amber-600">
              <Icon name="Clock" size={22} />
            </span>
          </div>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">
          {error}
        </div>
      )}

      <div className="rounded-xl border border-border bg-card shadow-sm">
        <div className="flex flex-wrap items-center gap-2 border-b border-border/80 px-4 py-4">
          {escrowStatusFilters.map((filter) => (
            <button
              key={filter.value}
              onClick={() => setStatusFilter(filter.value)}
              className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                statusFilter === filter.value
                  ? 'bg-primary text-primary-foreground shadow'
                  : 'bg-muted text-muted-foreground hover:text-foreground'
              }`}
            >
              {filter.label}
            </button>
          ))}
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-border text-sm">
            <thead className="bg-muted/30 text-muted-foreground">
              <tr>
                <th className="px-4 py-3 text-left font-semibold">Escrow ID</th>
                <th className="px-4 py-3 text-left font-semibold">Order</th>
                <th className="px-4 py-3 text-left font-semibold">Caterer</th>
                <th className="px-4 py-3 text-left font-semibold">Gross Amount</th>
                <th className="px-4 py-3 text-left font-semibold">Commission</th>
                <th className="px-4 py-3 text-left font-semibold">Net Amount</th>
                <th className="px-4 py-3 text-left font-semibold">Status</th>
                <th className="px-4 py-3 text-left font-semibold">Created</th>
                <th className="px-4 py-3 text-right font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/80">
              {loading && (
                <tr>
                  <td colSpan={9} className="px-6 py-6 text-center text-muted-foreground">
                    Loading escrows…
                  </td>
                </tr>
              )}

              {!loading && escrows.length === 0 && (
                <tr>
                  <td colSpan={9} className="px-6 py-6 text-center text-muted-foreground">
                    No escrows found.
                  </td>
                </tr>
              )}

              {escrows.map((escrow) => (
                <tr key={escrow.id} className="hover:bg-muted/30">
                  <td className="px-4 py-3 font-mono text-xs text-foreground">{escrow.id?.slice(0, 8)}...</td>
                  <td className="px-4 py-3 font-medium text-foreground">{escrow.order?.id?.slice(0, 8)}...</td>
                  <td className="px-4 py-3 text-muted-foreground">{escrow.caterer?.business_name || '—'}</td>
                  <td className="px-4 py-3 font-semibold text-foreground">
                    R {Number(escrow.gross_amount || 0).toLocaleString()}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">
                    R {Number(escrow.commission_amount || 0).toLocaleString()}
                  </td>
                  <td className="px-4 py-3 font-semibold text-foreground">
                    R {Number(escrow.caterer_net_amount || 0).toLocaleString()}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${
                      statusStyles[escrow.escrow_status] ?? 'bg-muted text-foreground'
                    }`}>
                      {escrow.escrow_status || 'unknown'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {escrow.created_at ? format(new Date(escrow.created_at), 'dd MMM yyyy') : '—'}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => {
                          getEscrowById(escrow.id).then((data) => {
                            setSelectedEscrow(data);
                            setShowReleaseModal(true);
                          }).catch((err) => toast.error(err?.message || 'Failed to load escrow'));
                        }}
                        className="inline-flex items-center gap-1 rounded-md border border-border px-3 py-1 text-xs font-semibold text-muted-foreground hover:bg-muted"
                      >
                        <Icon name="Eye" size={14} /> View
                      </button>
                      {escrow.escrow_status === 'escrowed' && (
                        <button
                          onClick={() => {
                            setSelectedEscrow(escrow);
                            setShowReleaseModal(true);
                          }}
                          className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-3 py-1 text-xs font-semibold text-white hover:bg-emerald-500"
                        >
                          <Icon name="Unlock" size={14} /> Release
                        </button>
                      )}
                      {escrow.escrow_status === 'disputed' && (
                        <button
                          onClick={() => handleResolveDispute(escrow.id)}
                          disabled={processing}
                          className="inline-flex items-center gap-1 rounded-md border border-border px-3 py-1 text-xs font-semibold text-foreground hover:bg-muted"
                        >
                          <Icon name="Shield" size={14} /> Resolve dispute
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Release Modal */}
      {showReleaseModal && selectedEscrow && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-xl max-w-2xl w-full p-6 shadow-lg">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-foreground">Release Escrow Funds</h2>
              <button
                onClick={() => {
                  setShowReleaseModal(false);
                  setSelectedEscrow(null);
                  setReleaseReason('');
                }}
                className="text-muted-foreground hover:text-foreground"
              >
                <Icon name="X" size={24} />
              </button>
            </div>
            <div className="space-y-4">
              <div className="bg-muted/30 rounded-lg p-4 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Escrow ID:</span>
                  <span className="font-mono text-xs">{selectedEscrow.id}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Order ID:</span>
                  <span className="font-mono text-xs">{selectedEscrow.order_id}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Caterer:</span>
                  <span>{selectedEscrow.caterer?.business_name || '—'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Release Amount:</span>
                  <span className="font-semibold text-lg">R {Number(selectedEscrow.caterer_net_amount || 0).toLocaleString()}</span>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Release Reason *</label>
                <textarea
                  value={releaseReason}
                  onChange={(e) => setReleaseReason(e.target.value)}
                  placeholder="Enter reason for releasing escrow funds..."
                  className="w-full border rounded-lg px-3 py-2 min-h-[100px]"
                  rows={4}
                />
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => {
                    setShowReleaseModal(false);
                    setSelectedEscrow(null);
                    setReleaseReason('');
                  }}
                  className="flex-1 bg-muted hover:bg-muted/80 text-foreground font-medium py-2 px-4 rounded-lg"
                  disabled={processing}
                >
                  Cancel
                </button>
                <button
                  onClick={handleReleaseEscrow}
                  disabled={processing || !releaseReason.trim()}
                  className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white font-medium py-2 px-4 rounded-lg disabled:opacity-50"
                >
                  {processing ? 'Processing...' : 'Release Funds'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
